import { apiErrorSchema, type ApiErrorCode } from "@gioia365/contracts";
import type { ZodType } from "zod";
import { COMMERCIAL_VIEW_HEADER, commercialViewOf, MEMBERSHIP_HEADER, PLATFORM_ORGANIZATION_HEADER, type TabContext } from "../session/tab-context";

// Il solo client HTTP delle API di Gioia365 (piano M1b D6). Ogni risposta passa dallo schema dei contratti,
// lo stesso che usa il server; ogni errore diventa `ApiRequestError` con il codice, da cui l'interfaccia
// prende il testo nei cataloghi (`messageForError`). Il `message` del server non arriva mai allo schermo.
// Better Auth resta fuori (`api/auth.ts`): ha una forma d'errore sua. Con la scheda (`tab`) partono i suoi header:
// l'azienda e, se la scheda guarda come un Commerciale, la Vista Commerciale (piano M2 D16).

export type ClientErrorCode = ApiErrorCode | "network" | "unexpected_response";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly details: Record<string, unknown> | undefined;
  /**
   * La Vista Commerciale con cui la richiesta è partita, oppure `null`: un rifiuto della vista
   * (`commercial_view_denied`) chiude la vista della scheda soltanto se è ancora quella (`handleQueryError`).
   */
  readonly commercialView: string | null;
  constructor(status: number, code: ClientErrorCode, details?: Record<string, unknown>, commercialView: string | null = null) {
    super(`API ${status} ${code}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.commercialView = commercialView;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export async function apiRequest<T>(
  method: Method,
  path: string,
  options: { schema: ZodType<T>; body?: unknown; tab?: TabContext | null; signal?: AbortSignal; keepalive?: boolean },
): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (options.tab?.kind === "membership") headers[MEMBERSHIP_HEADER] = options.tab.membershipId;
  if (options.tab?.kind === "platform") headers[PLATFORM_ORGANIZATION_HEADER] = options.tab.organizationId;
  const view = options.tab ? commercialViewOf(options.tab) : null;
  if (view !== null) headers[COMMERCIAL_VIEW_HEADER] = view;
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      cache: "no-store",
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      // Una richiesta piccola che deve arrivare anche se la pagina si chiude o si ricarica (il resoconto di TEL-33).
      ...(options.keepalive ? { keepalive: true } : {}),
    });
  } catch (error) {
    // Una richiesta annullata (cambio di azienda, pagina lasciata) non è un guasto della rete.
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiRequestError(0, "network", undefined, view);
  }
  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    if (parsed.success) throw new ApiRequestError(response.status, parsed.data.code, parsed.data.details, view);
    throw new ApiRequestError(response.status, "unexpected_response", undefined, view);
  }
  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) throw new ApiRequestError(response.status, "unexpected_response", undefined, view);
  return parsed.data;
}
